import { clone, find, lensProp, map, omit, over, pipe, prop, propEq, set, sort, when } from 'ramda';
import getCashierConfig from './cashierConfig';
import { numberPredicate } from 'js/utils/predicate.util';
import prepareValidationConfig from './prepareValidationConfig';

export const prepareSuggestions = when(
    ({ suggested }) => suggested,
    (methodSrc) => {
        const { suggested: { suggestion }, ...method } = methodSrc;
        const defaultSuggestion = pipe(
            find(prop('_default')),
            prop('value')
        )(suggestion);

        return {
            ...method,
            suggestions: suggestion.map(prop('value')),
            defaultSuggestion
        };
    }
);

/**
 * Attach config from cashierConfig
 */
export const attachCashierConfig = options => (method) => {
    const { paymentType } = options;
    const { code, depositType, reDirectType } = method;
    const isCreditCard = depositType === 'creditCard';
    const individualConfig = getCashierConfig({ paymentType, code });

    // COR-1734: generic is now any PM absent in cashier config if not set explicitly
    const isGeneric = !individualConfig || individualConfig.isGeneric;
    const config = individualConfig || getCashierConfig({ paymentType, code: 'generic' });
    let { validationConfig } = config;

    if (!validationConfig) {
        ({ validationConfig } = getCashierConfig({ paymentType, code: 'basic' }));
    }

    return {
        ...method,
        isGeneric,
        isCreditCard,
        ...omit(['validationConfig'], config),
        redirectType: reDirectType,
        validationConfig: clone(validationConfig)
    };
};

/**
 * Set-up multi-field account keys
 */
export const setUpMultiFieldConfig = (method) => {
    const { accountFields } = method;
    const keyFields = accountFields.filter(fld => fld.isAccountKey);
    const isNoAccountKeys = !keyFields.length;
    const isMultiFieldKey = keyFields.length > 1;

    return {
        ...method,
        keyFields,
        isNoAccountKeys,
        isMultiFieldKey
    };
};

/**
 * Check for isLabelField in accountFields to display in the account dropdown
 */
export const setUpLabelFieldConfig = (method) => {
    const { accountFields } = method;
    const labelField = find(propEq('isLabelField', true), accountFields);

    return {
        ...method,
        labelField
    };
};

export const convertToLowerCaseCVV2 = over(
    lensProp(['accountFields']),
    map(
        when(
            propEq('name', 'CVV2'),
            set(lensProp('name'), 'cvv2')
        )
    )
);

export const handleAccountFields = when(
    ({ accountFields }) => accountFields,
    pipe(
        setUpMultiFieldConfig,
        setUpLabelFieldConfig,
        convertToLowerCaseCVV2 // TODO: should be deleted after IMS done request
    )
);

export const getAccountDepositTime = account => new Date(account.lastDepositTime).getTime();

/**
 * Sort accounts. By default - by last deposit
 */
export const sortAccounts = when(
    ({ accounts }) => accounts,
    method => ({
        ...method,
        accounts: sort(numberPredicate(false, getAccountDepositTime), method.accounts)
    })
);

export const getCreditCardAccTitle = ({ accountData: { accountId, alias } }) => alias
    ? `${accountId} (${alias})`
    : accountId;

export const getCommonAccTitle = ({ accountFields = [], accountData = {} }) => {
    const key = accountFields.some(prop('isLabelField')) ? 'isLabelField' : 'isAccountKey';

    return accountFields
        .filter(prop(key))
        .map(({ name }) => accountData[name])
        .join('/');
};

/**
 * Generate account title
 */
export const generateAccountTitles = when(
    ({ accounts }) => accounts.length,
    (method) => {
        const { accounts, isCreditCard, accountFields } = method;
        const getTitle = isCreditCard ? getCreditCardAccTitle : getCommonAccTitle;

        return {
            ...method,
            accounts: accounts.map(acc => ({
                ...acc,
                title: getTitle({ ...acc, accountFields })
            }))
        };
    }
);

export default options => pipe(
    prepareSuggestions,
    attachCashierConfig(options),
    handleAccountFields,
    sortAccounts,
    generateAccountTitles,
    prepareValidationConfig
);
